import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  deletePlant,
  deleteWateringEvent,
  fetchPlant,
  fetchLastWatered,
  fetchHealthCheckups,
  markAsWatered,
  saveHealthCheckup,
  updateWateringEvent,
  updateWateringSchedule,
  type HealthCheckup,
  type Plant,
  type WateringEvent,
} from "../lib/plants";
import { isRemoteImageUrl, fileToBase64, uploadPlantImage } from "../lib/storage";
import { analyzeHealthCheckup } from "../lib/claude";
import {
  BackIcon,
  CameraIcon,
  DropletIcon,
  LeafIcon,
  SunIcon,
} from "../components/icons";

function formatShortDate(d: Date) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function daysAgoLabel(days: number) {
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function daysUntilLabel(days: number) {
  if (days <= 0) return "Water now";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

function healthPalette(status: string) {
  const s = (status || "").toLowerCase();
  if (s.includes("heal")) return { bg: "#dcf1e2", fg: "#1f5a39" };
  if (s.includes("mild")) return { bg: "#fef3c7", fg: "#92400e" };
  if (s.includes("seri") || s.includes("sick"))
    return { bg: "#fee2e2", fg: "#991b1b" };
  return { bg: "#e5e7eb", fg: "#374151" };
}

export default function PlantDetailScreen() {
  const navigate = useNavigate();
  const { plantId } = useParams<{ plantId: string }>();
  const [plant, setPlant] = useState<Plant | null>(null);
  const [lastEvent, setLastEvent] = useState<WateringEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [checkups, setCheckups] = useState<HealthCheckup[]>([]);
  const [healthHistoryOpen, setHealthHistoryOpen] = useState(false);
  const [checkupInFlight, setCheckupInFlight] = useState(false);
  const checkupInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!plantId) return;
    try {
      const [p, event, hc] = await Promise.all([
        fetchPlant(plantId),
        fetchLastWatered(plantId),
        fetchHealthCheckups(plantId),
      ]);
      setPlant(p);
      setLastEvent(event);
      setCheckups(hc);
    } catch (err) {
      console.error(err);
      alert("Could not load plant.");
    } finally {
      setLoading(false);
    }
  }, [plantId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleWater() {
    if (!plant || action) return;
    setAction(true);
    try {
      const updated = await markAsWatered(plant.id);
      setPlant(updated);
      setLastEvent(await fetchLastWatered(plant.id));
    } catch (err) {
      console.error(err);
      alert("Could not mark as watered.");
    } finally {
      setAction(false);
    }
  }

  async function handleAdjustSchedule() {
    if (!plant) return;
    const input = prompt(
      `Watering reminder is every ${plant.watering_frequency_days} days.\nEnter new frequency (1–90):`,
      String(plant.watering_frequency_days)
    );
    if (!input) return;
    const days = parseInt(input, 10);
    if (!days || days < 1 || days > 90) {
      alert("Enter a number between 1 and 90.");
      return;
    }
    setAction(true);
    try {
      const updated = await updateWateringSchedule(plant.id, days);
      setPlant(updated);
    } catch (err) {
      console.error(err);
      alert("Could not update schedule.");
    } finally {
      setAction(false);
    }
  }

  async function handleEditLastWatered() {
    if (!plant || !lastEvent) return;
    const input = prompt(
      "Enter the date you last watered (YYYY-MM-DD):",
      lastEvent.watered_at.split("T")[0]
    );
    if (!input) return;
    const newDate = new Date(input);
    if (isNaN(newDate.getTime())) {
      alert("Invalid date.");
      return;
    }
    setAction(true);
    try {
      const updated = await updateWateringEvent(lastEvent.id, plant.id, newDate);
      setPlant(updated);
      setLastEvent(await fetchLastWatered(plant.id));
    } catch (err) {
      console.error(err);
      alert("Could not update watering date.");
    } finally {
      setAction(false);
    }
  }

  async function handleUndoLastWatered() {
    if (!plant || !lastEvent) return;
    if (!confirm("Remove the last watering log?")) return;
    setAction(true);
    try {
      const updated = await deleteWateringEvent(lastEvent.id, plant.id);
      setPlant(updated);
      setLastEvent(await fetchLastWatered(plant.id));
    } catch (err) {
      console.error(err);
      alert("Could not remove watering log.");
    } finally {
      setAction(false);
    }
  }

  async function handleDelete() {
    if (!plant) return;
    if (
      !confirm(
        `Delete ${plant.nickname || plant.common_name}? This can't be undone.`
      )
    )
      return;
    setAction(true);
    try {
      await deletePlant(plant.id);
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert("Could not delete plant.");
      setAction(false);
    }
  }

  async function handleCheckupPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !plant) return;
    e.target.value = "";
    setCheckupInFlight(true);
    try {
      const [base64, imageUrl] = await Promise.all([
        fileToBase64(file),
        uploadPlantImage(file),
      ]);
      const mimeType = file.type.includes("png") ? "image/png" : "image/jpeg";
      const result = await analyzeHealthCheckup(
        base64,
        mimeType,
        plant.nickname || plant.common_name,
        {
          healthStatus: plant.health_status,
          diseases: plant.diseases,
          careSuggestions: plant.care_suggestions,
        }
      );
      await saveHealthCheckup(plant.id, imageUrl, result);
      await load();
      alert(result.comparisonNote);
    } catch (err) {
      console.error(err);
      alert("Health checkup failed. Try again.");
    } finally {
      setCheckupInFlight(false);
    }
  }

  if (loading) return <div className="loading-screen">Loading…</div>;
  if (!plant) return <div className="loading-screen">Plant not found</div>;

  const now = new Date();
  const nextWatering = new Date(plant.next_watering);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilWatering = (() => {
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const nextMidnight = new Date(nextWatering.getFullYear(), nextWatering.getMonth(), nextWatering.getDate());
    return Math.round((nextMidnight.getTime() - todayMidnight.getTime()) / msPerDay);
  })();
  const lastWateredDate = lastEvent
    ? new Date(lastEvent.watered_at)
    : null;
  const daysSinceWatered = lastWateredDate
    ? (() => {
        const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const b = new Date(lastWateredDate.getFullYear(), lastWateredDate.getMonth(), lastWateredDate.getDate());
        return Math.round((a.getTime() - b.getTime()) / msPerDay);
      })()
    : null;

  const health = healthPalette(plant.health_status);
  const tips = plant.care_suggestions || [];
  const diseases = plant.diseases || [];

  const heroUri = isRemoteImageUrl(plant.image_url)
    ? (plant.image_url as string)
    : "https://placehold.co/800x900/2d7a4f/ffffff/png?text=Plant";

  return (
    <div className="pd-root">
      <div className="pd-hero">
        <img src={heroUri} alt={plant.nickname || plant.common_name} />
        <div className="pd-hero-row">
          <button
            type="button"
            className="icon-btn translucent"
            onClick={() => navigate(-1)}
          >
            <BackIcon />
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              className="icon-btn translucent"
              onClick={() => navigate(`/plant/${plant.id}/edit`)}
              aria-label="Edit"
            >
              ✏️
            </button>
            <button
              type="button"
              className="icon-btn translucent"
              onClick={handleDelete}
              aria-label="Delete"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      <div className="pd-body">
        <div className="card pd-main">
          <div className="pd-main__header">
            <div style={{ flex: 1, paddingRight: 12 }}>
              <div className="pd-main__name">
                {plant.nickname || plant.common_name}
              </div>
              <div className="pd-main__sub">
                {plant.nickname ? plant.common_name : plant.scientific_name}
              </div>
            </div>
            <button
              type="button"
              className="pd-health"
              style={{ background: health.bg, color: health.fg }}
              onClick={() => setHealthHistoryOpen(!healthHistoryOpen)}
            >
              {(plant.health_status || "unknown").toUpperCase()}
              {checkups.length > 0 && <span className="pd-health__count">{checkups.length}</span>}
            </button>
          </div>

          <div className="pd-stats">
            <div className="stat-col">
              <div className="stat-icon">
                <DropletIcon />
              </div>
              <div className="stat-label">NEXT WATER</div>
              <div className="stat-value">
                {daysUntilLabel(daysUntilWatering)}
              </div>
            </div>
            <div className="stat-divider" />
            <div className="stat-col">
              <div className="stat-icon">
                <SunIcon />
              </div>
              <div className="stat-label">LIGHT</div>
              <div className="stat-value">{plant.light_needs || "—"}</div>
            </div>
          </div>

          <div className="pd-main__last">
            <span>
              Last watered:{" "}
              <strong>
                {lastWateredDate
                  ? `${formatShortDate(lastWateredDate)} (${daysAgoLabel(daysSinceWatered!)})`
                  : "Unknown"}
              </strong>
            </span>
            {lastEvent && (
              <span className="pd-main__last-actions">
                <button type="button" className="pd-mini-btn" onClick={handleEditLastWatered}>Edit</button>
                <button type="button" className="pd-mini-btn pd-mini-btn--danger" onClick={handleUndoLastWatered}>Undo</button>
              </span>
            )}
          </div>

          <button
            type="button"
            className="pd-adjust-btn"
            onClick={() => setScheduleOpen(!scheduleOpen)}
          >
            <span>
              Watering every {plant.watering_frequency_days}{" "}
              {plant.watering_frequency_days === 1 ? "day" : "days"}
            </span>
            <span className={`pd-chevron ${scheduleOpen ? "pd-chevron--open" : ""}`}>›</span>
          </button>

          {scheduleOpen && (
            <div className="pd-schedule">
              <div className="pd-schedule__row">
                <span className="pd-schedule__label">Next watering</span>
                <span className="pd-schedule__value">
                  {formatShortDate(nextWatering)} ({daysUntilLabel(daysUntilWatering)})
                </span>
              </div>
              <div className="pd-schedule__row">
                <span className="pd-schedule__label">Last watered</span>
                <span className="pd-schedule__value">
                  {lastWateredDate
                    ? `${formatShortDate(lastWateredDate)} (${daysAgoLabel(daysSinceWatered!)})`
                    : "Unknown"}
                </span>
              </div>
              <div className="pd-schedule__row">
                <span className="pd-schedule__label">Frequency</span>
                <span className="pd-schedule__value">
                  Every {plant.watering_frequency_days}{" "}
                  {plant.watering_frequency_days === 1 ? "day" : "days"}
                </span>
              </div>
              {plant.watering_amount && (
                <div className="pd-schedule__row">
                  <span className="pd-schedule__label">How much</span>
                  <span className="pd-schedule__value">{plant.watering_amount}</span>
                </div>
              )}
              {plant.watering_notes && (
                <div className="pd-schedule__note">
                  <DropletIcon size={14} color="#2D7A4F" />
                  <span>{plant.watering_notes}</span>
                </div>
              )}
              <button
                type="button"
                className="pd-schedule__adjust"
                onClick={handleAdjustSchedule}
              >
                Adjust frequency
              </button>
            </div>
          )}
        </div>

        {healthHistoryOpen && checkups.length > 0 && (
          <>
            <div className="section-label">HEALTH HISTORY</div>
            <div className="card pd-timeline">
              {checkups.map((c, i) => {
                const hp = healthPalette(c.health_status);
                const d = new Date(c.checked_at);
                return (
                  <div key={c.id} className={`pd-timeline__row ${i < checkups.length - 1 ? "pd-timeline__row--divider" : ""}`}>
                    <img src={c.image_url} alt="" className="pd-timeline__thumb" />
                    <div className="pd-timeline__info">
                      <div className="pd-timeline__date">
                        {d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                      <span className="pd-timeline__badge" style={{ background: hp.bg, color: hp.fg }}>
                        {c.health_status.toUpperCase()}
                      </span>
                      {c.comparison_note && (
                        <div className="pd-timeline__note">{c.comparison_note}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tips.length > 0 && (
          <>
            <div className="section-label">CARE TIPS</div>
            <div className="card tips-card">
              {tips.map((tip, i) => (
                <div
                  key={i}
                  className={`tip-row ${
                    i < tips.length - 1 ? "tip-row--divider" : ""
                  }`}
                >
                  <div className="tip-icon">
                    <LeafIcon size={16} color="#2D7A4F" />
                  </div>
                  <div className="tip-text">{tip}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {diseases.length > 0 && (
          <>
            <div className="section-label">ISSUES</div>
            <div className="card tips-card">
              {diseases.map((d, i) => (
                <div
                  key={i}
                  className={`tip-row ${
                    i < diseases.length - 1 ? "tip-row--divider" : ""
                  }`}
                >
                  <div className="tip-text">{d}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <input
        ref={checkupInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleCheckupPhoto}
      />

      {checkupInFlight && (
        <div className="pd-checkup-overlay">
          <div className="spinner" />
          <div className="pd-checkup-overlay__text">Analyzing health...</div>
        </div>
      )}

      <div className="sticky-cta-wrap">
        <button
          type="button"
          className="primary-btn pd-cta-btn"
          onClick={handleWater}
          disabled={action || checkupInFlight}
        >
          <DropletIcon size={18} color="#fff" />
          {action ? "…" : "Log"}
        </button>
        <button
          type="button"
          className="primary-btn pd-cta-btn pd-cta-update"
          onClick={() => checkupInputRef.current?.click()}
          disabled={action || checkupInFlight}
        >
          <CameraIcon size={16} color="#fff" />
          Update
        </button>
      </div>

      <style>{`
        .pd-root {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: #F0F5F0;
          padding-bottom: 120px;
          max-width: 480px;
          margin: 0 auto;
          width: 100%;
          position: relative;
        }
        .pd-hero {
          width: 100%;
          height: 50vh;
          max-height: 420px;
          background: #cbd5cb;
          position: relative;
          z-index: 0;
        }
        .pd-hero img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .pd-hero-row {
          position: absolute;
          top: env(safe-area-inset-top, 12px);
          left: 16px;
          right: 16px;
          display: flex;
          justify-content: space-between;
        }
        .icon-btn.translucent { background: rgba(255,255,255,0.85); }
        .pd-body {
          padding: 0 16px;
          margin-top: -28px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          position: relative;
          z-index: 1;
        }
        .pd-main {
          border-radius: 24px;
          padding: 20px;
        }
        .pd-main__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }
        .pd-main__name {
          font-family: Georgia, serif;
          font-size: 26px;
          font-weight: 700;
          color: #1f5a39;
          letter-spacing: 0.2px;
        }
        .pd-main__sub {
          font-size: 13px;
          font-style: italic;
          color: #2D7A4F;
          margin-top: 4px;
        }
        .pd-health {
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.8px;
          cursor: pointer;
          border: none;
          display: flex;
          align-items: center;
          gap: 6px;
          align-self: flex-start;
        }
        .pd-stats {
          display: flex;
          padding: 10px 0;
        }
        .stat-col { flex: 1; display: flex; flex-direction: column; gap: 6px; }
        .stat-divider { width: 1px; background: #d9e1da; margin: 0 12px; }
        .stat-icon {
          width: 36px;
          height: 36px;
          border-radius: 18px;
          background: #2D7A4F;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 4px;
        }
        .stat-label {
          font-size: 10px;
          font-weight: 700;
          color: #6b7a6e;
          letter-spacing: 0.6px;
        }
        .stat-value {
          font-size: 15px;
          font-weight: 700;
          color: #1a2e22;
        }
        .pd-main__last {
          margin-top: 18px;
          padding-top: 14px;
          border-top: 1px solid #d9e1da;
          font-size: 13px;
          color: #6b7a6e;
        }
        .pd-main__last {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .pd-main__last strong { color: #1a2e22; font-weight: 700; }
        .pd-main__last-actions { display: flex; gap: 8px; }
        .pd-mini-btn {
          background: none;
          border: 1px solid #dbe8df;
          border-radius: 6px;
          padding: 3px 8px;
          font-size: 11px;
          font-weight: 600;
          color: #2D7A4F;
          cursor: pointer;
        }
        .pd-mini-btn--danger { color: #c64545; border-color: #f0d0d0; }
        .pd-adjust-btn {
          width: 100%;
          margin-top: 14px;
          padding-top: 14px;
          border: none;
          border-top: 1px solid #d9e1da;
          background: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: #2D7A4F;
          font-weight: 600;
          cursor: pointer;
        }
        .pd-chevron {
          font-size: 18px;
          font-weight: 700;
          transition: transform 0.2s;
        }
        .pd-chevron--open {
          transform: rotate(90deg);
        }
        .pd-schedule {
          margin-top: 12px;
          padding: 14px;
          background: #f7faf8;
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .pd-schedule__row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .pd-schedule__label {
          font-size: 12px;
          color: #6b7a6e;
          font-weight: 600;
        }
        .pd-schedule__value {
          font-size: 13px;
          color: #1a2e22;
          font-weight: 700;
        }
        .pd-schedule__note {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 10px 12px;
          background: #e8f5ee;
          border-radius: 10px;
          font-size: 12px;
          color: #1f5a39;
          line-height: 18px;
          margin-top: 2px;
        }
        .pd-schedule__adjust {
          align-self: center;
          background: none;
          border: 1.5px solid #2D7A4F;
          border-radius: 999px;
          padding: 8px 20px;
          font-size: 12px;
          font-weight: 700;
          color: #2D7A4F;
          cursor: pointer;
          margin-top: 4px;
        }
        .tips-card { padding: 0; }
        .tip-row {
          display: flex;
          align-items: flex-start;
          padding: 14px 16px;
          gap: 12px;
        }
        .tip-row--divider { border-bottom: 1px solid #e6ede7; }
        .tip-icon {
          width: 32px;
          height: 32px;
          border-radius: 16px;
          background: #e6f0e8;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .tip-text { flex: 1; font-size: 14px; color: #1a2e22; line-height: 20px; }
        .pd-health__count {
          width: 18px;
          height: 18px;
          border-radius: 9px;
          background: rgba(0,0,0,0.15);
          font-size: 10px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .pd-timeline { padding: 0; }
        .pd-timeline__row {
          display: flex;
          gap: 12px;
          padding: 14px 16px;
          align-items: flex-start;
        }
        .pd-timeline__row--divider { border-bottom: 1px solid #e6ede7; }
        .pd-timeline__thumb {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .pd-timeline__info { flex: 1; display: flex; flex-direction: column; gap: 4px; }
        .pd-timeline__date { font-size: 12px; color: #6b7a6e; font-weight: 600; }
        .pd-timeline__badge {
          align-self: flex-start;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.5px;
        }
        .pd-timeline__note {
          font-size: 12px;
          color: #1a2e22;
          line-height: 17px;
          margin-top: 2px;
        }
        .pd-checkup-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          z-index: 100;
        }
        .pd-checkup-overlay__text {
          color: #fff;
          font-size: 15px;
          font-weight: 700;
        }
        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .sticky-cta-wrap { display: flex; gap: 10px; }
        .pd-cta-btn { flex: 1; }
        .pd-cta-update { background: #4a9e6e; }
      `}</style>
    </div>
  );
}
